import React, { useEffect, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import TextareaOnboarding from "components/onboarding/TextareaOnboarding";
import KeyboardAwareBottomButton from "components/onboarding/KeyboardAwareBottomButton";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { userApi } from "api/userApi";
import { useRecoilState, useRecoilValue } from "recoil";
import { useOnboardingPromotionData } from "../../../../hooks/useOnboardingPromotionData";
import { userSimpleAtom } from "../../../../recoil/userSimpleAtom";
import FullPageWithHeaderAndProgress from "../../../container/FullPageWithHeaderAndProgress";
import useAutoLoginPromotion from "../../../../hooks/useAutoLoginPromotion";
import { PATH } from "../../../../routes/paths";
import useMixpanelProfileMkt from "../../../../hooks/useMixpanelProfileMkt";
import useMixpanelMKT from "../../../../hooks/useMixpanelMKT";
import { webTestTypeAtom } from "../../../../recoil/webTestTypeAtom";

export default function NicknameSubmitPromotion() {
  const navigate = useNavigate();
  /* ➊ HTMLTextAreaElement 참조용 useRef 생성 */
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const { text, updateText } = useOnboardingPromotionData("nickname_submit");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [userPromotion, setUserPromotion] = useRecoilState(userSimpleAtom);
  const { isChecking } = useAutoLoginPromotion();

  const webTestType = useRecoilValue(webTestTypeAtom);

  const setPromotionPeople = useMixpanelProfileMkt("v4");

  const trackPromotion = useMixpanelMKT({
    eventName: "닉네임 제출 페이지 진입",
    version: "v4",
  });

  const { mutate: submit } = useMutation(
    (nickname: string) => userApi.submitNicknamePromotion(nickname),
    {
      onSuccess: (_, nickname) => {
        setLoading(false);

        if (webTestType?.type !== "a") {
          navigate("/promotion-onboarding/demographic/2", { replace: true });
        } else {
          navigate(PATH.promotionProduct, { replace: true });
        }

        setUserPromotion((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            nickname: nickname,
          };
        });

        setPromotionPeople({ $name: nickname });
      },
    }
  );

  function handleSubmit() {
    if (text && text.length >= 1) {
      setLoading(true);
      submit(text);
    }
  }

  /* ➋ 페이지 로드 후 자동으로 텍스트에어리어에 포커스 */
  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, []);

  function useEffectOnce(callback: () => void) {
    const ref = useRef(false);

    useEffect(() => {
      if (ref.current) return;
      ref.current = true;

      if (typeof callback === "function") {
        callback();
      }
    }, []);
  }

  useEffectOnce(() => {
    trackPromotion();
  });

  // 로그인 체크 및 리다이렉트
  useEffect(() => {
    if (isChecking) return;

    if (!userPromotion?.accessToken) {
      // 로그인 안 되어 있으면 로그인 페이지로
      navigate(PATH.promotionLogin, { replace: true });
    } else if (userPromotion?.nickname) {
      // nickname이 이미 있으면 결제 페이지로
      // if (webTestType?.type === "b") {
      //   navigate("/promotion-onboarding/demographic/2", { replace: true });
      // } else {
      //   navigate(PATH.promotionProduct, { replace: true });
      // }
    }
  }, [isChecking, userPromotion, navigate]);

  return (
    <FullPageWithHeaderAndProgress
      hideHeader={false}
      hideProgress={true}
      body={
        <Stack spacing={5} sx={{ height: "100%" }}>
          <CustomTypography
            variant="Subtitle"
            fontWeight={500}
            sx={{
              color: theme.vars.palette.Content.Strong,
            }}
          >
            디스턴싱에 오신 것을 환영해요. 코치 선생님이 나를 어떻게 부르면
            좋을까요?
          </CustomTypography>

          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
            sx={{ width: "100%" }}
          >
            <Box
              sx={{
                width: "100px",
                height: "54px",
                backgroundColor: theme.vars.palette.Surface.Forward,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                boxShadow: isFocused
                  ? `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`
                  : `inset 0 0 0 0`,
              }}
            >
              <TextareaOnboarding
                initialValue={text}
                handleChange={(newValue: string) => updateText(newValue)}
                isFocused={isFocused}
                setIsFocused={setIsFocused}
                emojiDisabled={true}
                readOnly={false}
                textAlign={"center"}
              />
            </Box>
            <Typography
              variant="Body"
              fontWeight={500}
              sx={{
                color: theme.vars.palette.Content.Strong,
              }}
            >
              님
            </Typography>
          </Stack>

          <KeyboardAwareBottomButton
            loading={loading || isChecking}
            disabled={isChecking || text === undefined || text === ""}
            onClick={handleSubmit}
            text={"다음"}
            highlightBackground={false}
          />
        </Stack>
      }
      highlightBackground={false}
      disableBack={false}
      onBack={() => navigate(-1)}
    />
  );
}
